import { type ApiError, type Section } from "~/api/types";

const urlBase = "/api/v2";

class UnauthorizedError extends Error {
  constructor() {
    super("Unauthorized");
    this.name = "UnauthorizedError";
  }
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

// The fetch layer has no access to React context, so the add-in subscribes
// here to learn when the session has expired.
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

function parseUnprocessableContentError(
  details?: Record<string, unknown>,
): string {
  const constraint = details?.constraint as string;
  const usedInDocuments = details?.usedInDocuments as string;

  switch (constraint) {
    case "section_in_use":
      return `Cannot delete section. It is currently used in: ${usedInDocuments}`;
    case "version_in_use":
      return `Cannot delete version. It is currently used in: ${usedInDocuments}`;
    case "last_version":
      return "Cannot delete the last version of a section. Delete the entire section instead.";
    default:
      return "Cannot delete section version";
  }
}

function parseApiError(
  error: { response: { data: ApiError } } | Error,
): string {
  if ("response" in error && error.response?.data?.code) {
    const data = error.response.data;

    if (data.code === "UNPROCESSABLE_CONTENT") {
      return parseUnprocessableContentError(data.details);
    }

    if (data.message) {
      return data.message;
    }

    return "An error occured";
  }
  return (error as Error).message ?? "Network error occurred";
}

const jsonHeaders = { "Content-Type": "application/json" };

async function handleJsonResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    unauthorizedListeners.forEach((listener) => listener());
    throw new UnauthorizedError();
  }

  if (!res.ok) {
    const contentType = res.headers.get("content-type");

    if (contentType?.includes("application/json")) {
      let data: ApiError;
      try {
        data = (await res.json()) as ApiError;
      } catch {
        throw new Error(`Request failed (${res.status})`);
      }

      const errorObj = {
        response: { data },
        message: (data?.details?.error as string) ?? "Request failed",
      };
      throw new Error(parseApiError(errorObj));
    }

    throw new Error(`Request failed (${res.status})`);
  }

  // Handle 204 No Content responses (no body expected)
  if (res.status === 204) {
    return null as T;
  }

  return res.json() as T;
}

export async function getJson<T>(path: string): Promise<T> {
  const url = `${urlBase}${path}`;
  const options: RequestInit = {
    method: "GET",
    headers: jsonHeaders,
  };
  const res = await fetch(url, options);
  return handleJsonResponse(res);
}

async function postJson<T>(
  path: string,
  body: CreateSectionRequest | ReportSectionsUsageRequest,
): Promise<T> {
  const url = `${urlBase}${path}`;
  const options: RequestInit = {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  };
  const res = await fetch(url, options);
  return handleJsonResponse<T>(res);
}

async function putJson<T>(
  path: string,
  body: UpdateSectionRequest,
): Promise<T> {
  const url = `${urlBase}${path}`;
  const options: RequestInit = {
    method: "PUT",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  };
  const res = await fetch(url, options);
  return handleJsonResponse<T>(res);
}

async function deleteJson<T>(path: string): Promise<T> {
  const url = `${urlBase}${path}`;
  const options: RequestInit = {
    method: "DELETE",
    headers: jsonHeaders,
  };
  const res = await fetch(url, options);
  return handleJsonResponse<T>(res);
}

interface CreateSectionRequest {
  name: string;
  htmlContent: string;
  xmlContent: string;
}

interface UpdateSectionRequest {
  htmlContent: string;
  xmlContent: string;
}

interface ReportSectionsUsageRequest {
  artifactId: number;
  sectionVersionIds: number[];
}

const ENDPOINT_BASE = "/sections";

export function getSectionById(id: number) {
  return getJson<Section>(`${ENDPOINT_BASE}/${id}`);
}
export function updateSection(
  id: number,
  htmlContent: string,
  xmlContent: string,
) {
  const requestBody: UpdateSectionRequest = {
    htmlContent,
    xmlContent,
  };
  return putJson<Section>(`${ENDPOINT_BASE}/${id}`, requestBody);
}
export function createSection(
  name: string,
  htmlContent: string,
  xmlContent: string,
) {
  const requestBody: CreateSectionRequest = {
    name,
    htmlContent,
    xmlContent,
  };
  return postJson<Section>(`${ENDPOINT_BASE}`, requestBody);
}

export function deleteSection(id: number) {
  return deleteJson<null>(`${ENDPOINT_BASE}/${id}`);
}

export function deleteVersion(sectionId: number, versionId: number) {
  return deleteJson<null>(
    `${ENDPOINT_BASE}/${sectionId}/versions/${versionId}`,
  );
}

export function resolveDocumentArtifactId(officeUrl: string) {
  const params = new URLSearchParams({ url: officeUrl });
  return getJson<{ documentArtifactId: number | null }>(
    `/document-artifacts/resolve-document-id-from-office-url?${params.toString()}`,
  );
}

export function reportSectionsUsage(
  artifactId: number,
  sectionVersionIds: number[],
) {
  const requestBody: ReportSectionsUsageRequest = {
    artifactId,
    sectionVersionIds: sectionVersionIds,
  };
  return postJson<void>(`${ENDPOINT_BASE}/report-sections-usage`, requestBody);
}
